import { useEffect, useRef, useState } from 'react';

/**
 * Hook pour détecter quand un élément entre dans la vue
 * et déclencher une animation
 */
export const useScrollAnimation = (options = {}) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    const {
        threshold = 0.1,
        rootMargin = '0px 0px -50px 0px',
        triggerOnce = true
    } = options;

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (triggerOnce) {
                        observer.unobserve(element);
                    }
                } else if (!triggerOnce) {
                    setIsVisible(false);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(element);

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [threshold, rootMargin, triggerOnce]);

    return [ref, isVisible];
};

/**
 * Hook pour animer plusieurs éléments avec un délai entre chacun
 */
export const useStaggerAnimation = (itemCount, baseDelay = 100) => {
    const [visibleItems, setVisibleItems] = useState([]);
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Animer les éléments un par un avec un délai
                    for (let i = 0; i < itemCount; i++) {
                        setTimeout(() => {
                            setVisibleItems(prev => [...prev, i]);
                        }, i * baseDelay);
                    }
                    observer.unobserve(container);
                }
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        observer.observe(container);

        return () => {
            if (container) {
                observer.unobserve(container);
            }
        };
    }, [itemCount, baseDelay]);

    return [containerRef, visibleItems];
};

export default useScrollAnimation;
